import React, { useEffect, useState } from 'react';
import AdminDashboard from 'layouts/AdminDashboard';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import {
  getAdminAssignments,
  updateAdminAssignment,
  getAdminQuestions,
  createAdminQuestion,
  updateAdminQuestion,
  deleteAdminQuestion,
} from './DocketAdminService';

const PROGRAMME_DISPLAY: Record<string, string> = {
  OPTIMISE_MY_FINANCES: 'Optimise My Finances',
  MORTGAGE_PREPARATION: 'Mortgage Preparation',
  TAX_PREPARATION: 'Tax Preparation',
  HEALTH_RECORDS: 'Health Records',
  MEDICATION_TRACKING: 'Medication Tracking',
  ESTATE_PLANNING: 'Estate Planning',
  CONTRACTS: 'Contracts',
};

const EMPTY_QUESTION = {
  questionText: '',
  questionType: 'TEXT',
  options: '',
  sequenceOrder: 1,
  required: false,
};

const DocketAssignmentDetail = (): JSX.Element => {
  const { programmeCode, assignmentId } = useParams<{
    programmeCode: string;
    assignmentId: string;
  }>();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState<any | null>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState<any>({
    title: '',
    description: '',
    instructions: '',
    assignmentType: 'DOCUMENT',
    sequenceOrder: 1,
    groupCode: '',
    uploadRequired: false,
    required: true,
  });

  // Question dialogs
  const [questionForm, setQuestionForm] = useState({ ...EMPTY_QUESTION });
  const [addQOpen, setAddQOpen] = useState(false);
  const [editQ, setEditQ] = useState<any | null>(null);
  const [deleteQId, setDeleteQId] = useState<number | null>(null);
  const [deleteQOpen, setDeleteQOpen] = useState(false);
  const [savingQ, setSavingQ] = useState(false);

  useEffect(() => {
    if (programmeCode && assignmentId) {
      loadData();
    }
  }, [programmeCode, assignmentId]);

  function loadData() {
    setLoading(true);
    Promise.all([
      getAdminAssignments(programmeCode!).then((res) => {
        const found = res.data.find((a: any) => String(a.id) === assignmentId);
        if (found) {
          setAssignment(found);
          setForm({
            title: found.title || '',
            description: found.description || '',
            instructions: found.instructions || '',
            assignmentType: found.assignmentType || 'DOCUMENT',
            sequenceOrder: found.sequenceOrder || 1,
            groupCode: found.groupCode || '',
            uploadRequired: found.uploadRequired || false,
            required: found.required !== false,
          });
        }
      }),
      getAdminQuestions(Number(assignmentId)).then((res) =>
        setQuestions(res.data),
      ),
    ])
      .catch(() => setError('Failed to load assignment.'))
      .finally(() => setLoading(false));
  }

  function handleSave() {
    setSaving(true);
    setSuccess(false);
    updateAdminAssignment(Number(assignmentId), form)
      .then(() => setSuccess(true))
      .catch(() => setError('Failed to save.'))
      .finally(() => setSaving(false));
  }

  function handleAddQuestion() {
    setSavingQ(true);
    const payload = {
      ...questionForm,
      options:
        questionForm.questionType === 'MULTI_CHOICE'
          ? questionForm.options
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
          : [],
    };
    createAdminQuestion(Number(assignmentId), payload)
      .then((res) => {
        setQuestions((prev) => [...prev, res.data]);
        setAddQOpen(false);
        setQuestionForm({ ...EMPTY_QUESTION });
      })
      .catch(() => setError('Failed to add question.'))
      .finally(() => setSavingQ(false));
  }

  function handleEditQuestion() {
    if (!editQ) return;
    setSavingQ(true);
    const payload = {
      ...questionForm,
      options:
        questionForm.questionType === 'MULTI_CHOICE'
          ? questionForm.options
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
          : [],
    };
    updateAdminQuestion(editQ.id, payload)
      .then((res) => {
        setQuestions((prev) =>
          prev.map((q) => (q.id === editQ.id ? res.data : q)),
        );
        setEditQ(null);
      })
      .catch(() => setError('Failed to update question.'))
      .finally(() => setSavingQ(false));
  }

  function handleDeleteQuestion() {
    if (deleteQId == null) return;
    deleteAdminQuestion(deleteQId)
      .then(() => {
        setQuestions((prev) => prev.filter((q) => q.id !== deleteQId));
        setDeleteQOpen(false);
        setDeleteQId(null);
      })
      .catch(() => setError('Failed to delete question.'));
  }

  function openEditQuestion(q: any) {
    setEditQ(q);
    setQuestionForm({
      questionText: q.questionText || '',
      questionType: q.questionType || 'TEXT',
      options: (q.options || []).join(', '),
      sequenceOrder: q.sequenceOrder || 1,
      required: q.required || false,
    });
  }

  const displayName = PROGRAMME_DISPLAY[programmeCode!] || programmeCode;

  return (
    <Container>
      <AdminDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/admin/docket">
              Docket
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href={`/admin/docket/programme/${programmeCode}`}
            >
              {displayName}
            </Link>
            <Typography color="text.primary">Assignment</Typography>
          </Breadcrumbs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Saved successfully.
          </Alert>
        )}
        {loading && <Typography>Loading...</Typography>}

        {assignment && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={4}
            >
              <Typography variant="h4">{assignment.title}</Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(`/admin/docket/programme/${programmeCode}`)
                  }
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>

            <Box component="form" mb={6}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Title
                  </Typography>
                  <TextField
                    fullWidth
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Assignment Type
                  </Typography>
                  <TextField
                    fullWidth
                    value={form.assignmentType}
                    onChange={(e) =>
                      setForm({ ...form, assignmentType: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Description
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Instructions
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={form.instructions}
                    onChange={(e) =>
                      setForm({ ...form, instructions: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Sequence Order
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={form.sequenceOrder}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        sequenceOrder: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Group Code
                  </Typography>
                  <TextField
                    fullWidth
                    value={form.groupCode}
                    onChange={(e) =>
                      setForm({ ...form, groupCode: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box display="flex" flexDirection="column" gap={1} mt={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.uploadRequired}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              uploadRequired: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Upload Required"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.required}
                          onChange={(e) =>
                            setForm({ ...form, required: e.target.checked })
                          }
                        />
                      }
                      label="Required"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Questions Section */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" fontWeight={600}>
                Questions
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setQuestionForm({ ...EMPTY_QUESTION });
                  setAddQOpen(true);
                }}
              >
                Add Question
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table>
                <TableHead sx={{ bgcolor: 'alternate.main' }}>
                  <TableRow>
                    {[
                      'Order',
                      'Question',
                      'Type',
                      'Options',
                      'Required',
                      'Actions',
                    ].map((h) => (
                      <TableCell key={h}>
                        <Typography
                          color="text.secondary"
                          variant="caption"
                          fontWeight={700}
                          sx={{ textTransform: 'uppercase' }}
                        >
                          {h}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="text.secondary" variant="body2">
                          No questions yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {questions.map((q) => (
                    <TableRow key={q.id}>
                      <TableCell>{q.sequenceOrder}</TableCell>
                      <TableCell>{q.questionText}</TableCell>
                      <TableCell>{q.questionType}</TableCell>
                      <TableCell>
                        {(q.options || []).join(', ') || '—'}
                      </TableCell>
                      <TableCell>{q.required ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => openEditQuestion(q)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeleteQId(q.id);
                            setDeleteQOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Add Question Dialog */}
        <Dialog
          open={addQOpen}
          onClose={() => setAddQOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Question</DialogTitle>
          <DialogContent>
            <QuestionForm form={questionForm} onChange={setQuestionForm} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddQOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddQuestion}
              disabled={savingQ || !questionForm.questionText}
            >
              {savingQ ? 'Saving...' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Question Dialog */}
        <Dialog
          open={!!editQ}
          onClose={() => setEditQ(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Question</DialogTitle>
          <DialogContent>
            <QuestionForm form={questionForm} onChange={setQuestionForm} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditQ(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleEditQuestion}
              disabled={savingQ || !questionForm.questionText}
            >
              {savingQ ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Question Confirmation */}
        <Dialog open={deleteQOpen} onClose={() => setDeleteQOpen(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>Delete this question?</DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteQOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteQuestion} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </AdminDashboard>
    </Container>
  );
};

interface QuestionFormProps {
  form: any;
  onChange: (f: any) => void;
}

const QuestionForm = ({ form, onChange }: QuestionFormProps): JSX.Element => (
  <Box display="flex" flexDirection="column" gap={2} mt={1}>
    <TextField
      label="Question Text"
      fullWidth
      multiline
      rows={2}
      value={form.questionText}
      onChange={(e) => onChange({ ...form, questionText: e.target.value })}
    />
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Question Type
      </Typography>
      <Select
        fullWidth
        value={form.questionType}
        onChange={(e) => onChange({ ...form, questionType: e.target.value })}
      >
        <MenuItem value="TEXT">Text (free text answer)</MenuItem>
        <MenuItem value="MULTI_CHOICE">Multiple Choice</MenuItem>
      </Select>
    </Box>
    {form.questionType === 'MULTI_CHOICE' && (
      <TextField
        label="Options (comma-separated)"
        fullWidth
        value={form.options}
        onChange={(e) => onChange({ ...form, options: e.target.value })}
        helperText="e.g. Option A, Option B, Option C"
      />
    )}
    <TextField
      label="Sequence Order"
      type="number"
      fullWidth
      value={form.sequenceOrder}
      onChange={(e) =>
        onChange({ ...form, sequenceOrder: parseInt(e.target.value) || 1 })
      }
    />
    <FormControlLabel
      control={
        <Checkbox
          checked={form.required}
          onChange={(e) => onChange({ ...form, required: e.target.checked })}
        />
      }
      label="Required"
    />
  </Box>
);

export default DocketAssignmentDetail;
